import { useMemo } from "react";
import type { MenuModal } from "../types/layout";
import AppSubMenu from "./AppSubMenu";
import { useHasRole } from "../hooks/useHasRole";
import { isOnboardingEnabled } from "../config/featureFlags";

const AppMenu = () => {
  const canViewBankTransactions = useHasRole("bank");
  const canViewStationStatus = useHasRole("admin");

  // Memoised because AppSubMenu regenerates the breadcrumbs whenever the model
  // changes identity. A fresh array literal each render would set breadcrumb
  // state on every render, which re-renders this tree and loops.
  const model: MenuModal[] = useMemo(
    () => [
    {
      label: "Dashboard",
      icon: "pi pi-home",
      items: [
        {
          label: "Home",
          icon: "pi pi-fw pi-home",
          to: "/",
        },
        {
          label: "Operations Review",
          icon: "pi pi-fw pi-chart-bar",
          to: "/operations",
        },
        {
          label: "App Users",
          icon: "pi pi-fw pi-user",
          to: "/users",
        },
        {
          label: "Campaigns",
          icon: "pi pi-fw pi-megaphone",
          to: "/campaigns",
        },
      ],
    },
    { separator: true },
    {
      // BOF-76: the home for analytics screens. Charging Stations replaces the hand-kept workbook.
      label: "Analytics",
      icon: "pi pi-chart-bar",
      items: [
        {
          // CP-388's per-station analytics, moved here from Station Management.
          label: "Station Performance",
          icon: "pi pi-fw pi-chart-line",
          to: "/analytics/station-performance",
        },
        {
          label: "Charging Stations",
          icon: "pi pi-fw pi-bolt",
          to: "/analytics/charging-stations",
        },
      ],
    },
    { separator: true },
    {
      label: "Station Management",
      icon: "pi pi-map-marker",
      items: [
        {
          label: "Station List",
          icon: "pi pi-fw pi-list",
          to: "/station-list",
        },
        ...(canViewStationStatus
          ? [
              {
                // CLR-281: payout figures, admin only.
                label: "THG Payouts",
                icon: "pi pi-fw pi-euro",
                to: "/thg-annual-summary",
              },
            ]
          : []),
        {
          label: "QR Code Management",
          icon: "pi pi-fw pi-qrcode",
          to: "/qr-codes",
        },
        {
          label: "RFID Cards",
          icon: "pi pi-fw pi-credit-card",
          to: "/rfid-cards",
        },
        {
          label: "Target Groups",
          icon: "pi pi-fw pi-sitemap",
          to: "/target-groups",
        },
        ...(canViewStationStatus
          ? [
              {
                label: "Station Status",
                icon: "pi pi-fw pi-exclamation-triangle",
                to: "/alerting/status",
              },
              {
                label: "Alert Exclusions",
                icon: "pi pi-fw pi-ban",
                to: "/alerting/exclusions",
              },
            ]
          : []),
      ],
    },
    { separator: true },
    {
      label: "Financial",
      icon: "pi pi-money-bill",
      items: [
        {
          label: "Clearing",
          icon: "pi pi-fw pi-shield",
          items: [
            {
              label: "Overview",
              icon: "pi pi-fw pi-list",
              to: "/clearing",
            },
            {
              label: "Credit Notes",
              icon: "pi pi-fw pi-spinner",
              to: "/clearing/credit-notes",
            },
            ...(canViewBankTransactions
              ? [
                  {
                    label: "Bank Transactions",
                    icon: "pi pi-fw pi-money-bill",
                    to: "/clearing/bank-transactions",
                  },
                ]
              : []),
          ],
        },
        {
          label: "Customers",
          icon: "pi pi-fw pi-users",
          to: "/customers",
        },
        {
          label: "Default Prices",
          icon: "pi pi-fw pi-dollar",
          to: "/default-prices",
        },
        {
          label: "Community Prices",
          icon: "pi pi-fw pi-dollar",
          to: "/community-prices",
        },
        {
          label: "Payments",
          icon: "pi pi-fw pi-receipt",
          to: "/transactions",
        },
        {
          label: "Wallet Admin",
          icon: "pi pi-fw pi-wallet",
          to: "/wallet-admin",
        },
      ],
    },
    { separator: true },
    ...(canViewStationStatus
      ? [
          {
            label: "Notifications",
            icon: "pi pi-bell",
            items: [
              {
                label: "Station Subscriptions",
                icon: "pi pi-fw pi-bell",
                to: "/notifications/subscriptions",
              },
              {
                label: "Setting Changes",
                icon: "pi pi-fw pi-history",
                to: "/notifications/audit",
              },
            ],
          },
          { separator: true },
        ]
      : []),
    {
      label: "Accounting",
      icon: "pi pi-receipt",
      items: [
        {
          label: "DATEV Exports",
          icon: "pi pi-fw pi-download",
          to: "/accounting/datev-exports",
        },
      ],
    },
    // Hidden until the onboarding flag is turned on. The separator goes with
    // the group so the sidebar never shows two rules in a row.
    ...(isOnboardingEnabled
      ? [
          { separator: true },
          {
            label: "Onboarding",
            icon: "pi pi-inbox",
            items: [
              {
                label: "Prospects",
                icon: "pi pi-fw pi-users",
                to: "/onboarding/prospects",
              },
              {
                label: "Wallbox Catalogue",
                icon: "pi pi-fw pi-bolt",
                to: "/onboarding/wallbox",
              },
              {
                label: "eRound Provisioning",
                icon: "pi pi-fw pi-send",
                to: "/onboarding/eround-provisioning",
              },
              {
                label: "Resign Reasons",
                icon: "pi pi-fw pi-times-circle",
                to: "/onboarding/resign-reasons",
              },
              {
                label: "Analytics",
                icon: "pi pi-fw pi-chart-line",
                to: "/onboarding/analytics",
              },
              {
                label: "Contract Analytics",
                icon: "pi pi-fw pi-chart-bar",
                to: "/onboarding/contract-analytics",
              },
              {
                label: "Manufacturer Configuration",
                icon: "pi pi-fw pi-file-edit",
                to: "/onboarding/vendor-configs",
              },
              {
                label: "Portal Settings",
                icon: "pi pi-fw pi-link",
                to: "/onboarding/portal-settings",
              },
            ],
          },
        ]
      : []),
    { separator: true },
    {
      label: "Settings",
      icon: "pi pi-cog",
      items: [
        {
          label: "App Version",
          icon: "pi pi-fw pi-mobile",
          to: "/app-version",
        },
        {
          label: "AI Budget",
          icon: "pi pi-fw pi-microchip-ai",
          to: "/ai-budget",
        },
          ],
        },
      ],
    [canViewBankTransactions, canViewStationStatus]
  );

  return <AppSubMenu model={model} />;
};

export default AppMenu;
